import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { createSelector } from 'reselect';
import { createReducer } from '../reducers/factory';
import radarRecommendations from '../api/radarRecommendations';
import { addFriend } from './radarFriends';
import {
  CLEAR_RADAR_USER_DATA,
  decreaseSwipesCount,
  fetchRadarUserProfileAction,
  increaseSwipesCount,
  radarUserDataPlainSelector,
  setSwipesCount,
  updateSuperlikeBalance,
} from './radarUserProfile';
import { LOGOUT_SUCCESSFUL } from '../reducers/auth';
import { isFutureAndTruthy, isSameUTCDay } from '../utils/dateAndTime';

const sliceName = 'radarRecommendations';
const reducerInfix = 'RECOMMENDATIONS';

const TAKE_RECOMMENDATION = 'TAKE_RADAR_RECOMMENDATION';
const UNTAKE_RECOMMENDATION = 'UNTAKE_RADAR_RECOMMENDATION';
const QUEUE_MATCH = 'QUEUE_RADAR_MATCH';
const DROP_MATCH = 'DROP_RADAR_MATCH';
const FETCH_SUCCESSFUL = `FETCH_${reducerInfix}_SUCCESSFUL`;

const FETCH_TIMEOUT = 30 * 1000;

const fetchReducer = createReducer(reducerInfix, {
  fetchTimeout: FETCH_TIMEOUT,
  invalidateActions: ['LOGIN_SUCCESSFUL'],
  minUpdate: 5 * 60 * 1000,
  extendedReducer: {
    [FETCH_SUCCESSFUL]: (state, action) => {
      const { items, currentCardIdRef } = action;

      let newList;

      // if top card available in fetched recommendation use it as a first
      let firstCard = items.find(
        (card) => card.id === currentCardIdRef?.current,
      );

      if (!firstCard) {
        // if not use current top card
        firstCard = state.items.find(
          (card) => card.id === currentCardIdRef?.current,
        );
      }

      if (firstCard?.id) {
        newList = [
          firstCard,
          ...items.filter((card) => card.id !== firstCard.id),
        ];
      } else {
        newList = items;
      }

      // instead of filtering out mark as taken to prevent that recommendations
      // that will be fetched next will include them again, not updated - not taken
      newList = newList.map((card) =>
        state.items.find((oldCard) => oldCard.id === card.id && oldCard.taken)
          ? { ...card, taken: true }
          : card,
      );

      return {
        ...state,
        items: newList,
        fetchingInProgressSince: null,
        lastSuccessfulFetch: action.timestamp,
        fetchPromise: null,
        didInvalidate: false,
      };
    },
  },
  initialState: {
    // list of recommendations
    items: [],

    // list of friends that were matched and should trigger animation
    matches: [],
  },
});

const fetchSuccessful = (items, currentCardIdRef) => ({
  type: FETCH_SUCCESSFUL,
  items,
  currentCardIdRef,
  timestamp: Date.now(),
});

const RecommendationsReducer = (
  state = fetchReducer.INITIAL_STATE,
  action = {},
) => {
  switch (action.type) {
    case LOGOUT_SUCCESSFUL:
    case CLEAR_RADAR_USER_DATA:
      return { ...fetchReducer.INITIAL_STATE };
    case TAKE_RECOMMENDATION:
      return {
        ...state,
        // or remove
        items: state.items.map((item) =>
          item.id !== action.id ? item : { ...item, taken: true },
        ),
      };
    case UNTAKE_RECOMMENDATION: {
      const untakenItem = state.items.find((item) => item.id === action.id);
      return {
        ...state,
        items: [
          ...state.items.filter((item) => item.id !== action.id),
          { ...untakenItem, taken: false },
        ],
      };
    }
    case QUEUE_MATCH:
      return {
        ...state,
        matches: [...state.matches, action.friend],
      };
    case DROP_MATCH:
      return {
        ...state,
        matches: state.matches.filter((item) => item.id !== action.id),
      };
    default:
      return fetchReducer(state, action);
  }
};

const fetchRecommendationsAction =
  (currentCardIdRef) => async (dispatch, getState) => {
    const {
      auth: { token },
    } = getState();

    if (!token) {
      return null;
    }

    const fetchPromise = (async () => {
      try {
        const fetchedCards = await radarRecommendations.fetchRecommendations(
          token,
        );
        dispatch(fetchSuccessful(fetchedCards, currentCardIdRef));
        return null;
      } catch (error) {
        dispatch(fetchReducer.fetchFailed(error));
        return null;
      }
    })();

    dispatch(fetchReducer.fetchInProgress(fetchPromise));
    return fetchPromise;
  };

export const fetchRecommendationsIfNeeded =
  (forceUpdate, currentCardIdRef) => async (dispatch, getState) => {
    const {
      [sliceName]: { fetchPromise, items, ...state },
    } = getState();

    if (fetchReducer.shouldFetch(state)) {
      return dispatch(fetchRecommendationsAction(currentCardIdRef));
    }

    if (
      forceUpdate &&
      (!state.fetchingInProgressSince ||
        Date.now() - state.fetchingInProgressSince > FETCH_TIMEOUT)
    ) {
      return dispatch(fetchRecommendationsAction(currentCardIdRef));
    }

    return fetchPromise?.then === 'function' ? fetchPromise : items;
  };

export const takeRecommendation = (id) => ({
  type: TAKE_RECOMMENDATION,
  id,
});

const untakeRecommendation = (id) => ({
  type: UNTAKE_RECOMMENDATION,
  id,
});

const queueMatch = (friend) => ({
  type: QUEUE_MATCH,
  friend,
});

export const dropMatch = (id) => ({
  type: DROP_MATCH,
  id,
});

export const dislikeRecommendation = (id) => async (dispatch, getState) => {
  const {
    auth: { token },
    [sliceName]: { items },
  } = getState();

  if (!token || !id) {
    return;
  }

  const currentRecommendation = items.find((item) => item.id === id);
  const superLiked = !!currentRecommendation?.superLiked;

  try {
    await radarRecommendations.dislikeRecommendation(token, id, superLiked);
  } catch (error) {
    // ignore "already swiped" and "forbidden error" and "resource not found" response - skip
    if (![400, 403, 404].includes(error?.status)) {
      dispatch(untakeRecommendation(id));
      throw error;
    }
  }
};

export const likeRecommendation = (id) => async (dispatch, getState) => {
  const {
    auth: { token },
    radarFriends: { items: friends },
    radarUserProfile: { userData } = {},
    radarConfig: { config } = {},
  } = getState();

  if (!token || !id) {
    return {};
  }

  const isSubscriptionActive = isFutureAndTruthy(
    userData?.subscriptionValidUntil,
  );

  if (
    !isSubscriptionActive &&
    config?.limits?.dailySwipes &&
    userData?.swipesCount >= config?.limits?.dailySwipes
  ) {
    if (isSameUTCDay(userData.lastSwipeAt, new Date())) {
      // cause showing popup immediately
      return { swipesLimitReached: true };
    }
    // clear counter to 1 and allow for swipe because of new day
    dispatch(setSwipesCount(1));
  } else {
    dispatch(increaseSwipesCount());
  }

  try {
    const response = await radarRecommendations.likeRecommendation(token, id);
    if (response?.friend?.cuid) {
      // matched
      if (!friends.includes((friend) => friend.cuid === response.friend.cuid)) {
        dispatch(addFriend(response.friend));
      }
      dispatch(queueMatch(response.friend));
    }
  } catch (error) {
    if (error?.status === 400 && error?.errorCode === 40021) {
      // limit reached - fetch user because data not synchronized
      dispatch(fetchRadarUserProfileAction());
      dispatch(untakeRecommendation(id));
      return { swipesLimitReached: true };
    }
    dispatch(decreaseSwipesCount());
    // ignore "already swiped" and "forbidden error" and "resource not found" response - skip
    if (![400, 403, 404].includes(error?.status)) {
      dispatch(untakeRecommendation(id));
      throw error;
    }
  }

  return {};
};

export const superlikeRecommendation = (id) => async (dispatch, getState) => {
  const {
    auth: { token },
    radarFriends: { items: friends },
  } = getState();

  if (!token || !id) {
    return;
  }

  try {
    const response = await radarRecommendations.superlikeRecommendation(
      token,
      id,
    );
    if (response?.friend?.cuid) {
      // matched
      if (!friends.includes((friend) => friend.cuid === response.friend.cuid)) {
        dispatch(addFriend(response.friend));
      }
      dispatch(queueMatch(response.friend));
    }
    if (
      typeof response.superLikeBalance === 'number' &&
      response.dailySuperLikeUsedAt
    ) {
      dispatch(
        updateSuperlikeBalance(
          response.superLikeBalance,
          response.dailySuperLikeUsedAt,
        ),
      );
    }
  } catch (error) {
    if (error?.status === 400 && error?.errorCode === 40001) {
      // limit reached - fetch user because data not synchronized
      dispatch(fetchRadarUserProfileAction());
      dispatch(untakeRecommendation(id));
      throw error;
    }
    // ignore "already swiped" and "forbidden error" and "resource not found" response - skip
    if (![400, 403, 404].includes(error?.status)) {
      dispatch(untakeRecommendation(id));
      throw error;
    }
  }
};

export const matchesSelector = ({ [sliceName]: { matches } }) => matches;
const recommendationsSelector = ({ [sliceName]: { items } }) =>
  items.filter((item) => item?.status === 'awaiting' && !item.taken);
export const recommendationsFetchedSelector = ({
  [sliceName]: { lastSuccessfulFetch },
}) => lastSuccessfulFetch;

export const recommendationSelectorFactory = (id) =>
  createSelector(
    ({ [sliceName]: { items } }) => items,
    (items) => (items || []).find((item) => item.id === id),
  );

export const reportingEnabledSelector = createSelector(
  radarUserDataPlainSelector,
  recommendationsSelector,
  ({ userData }, recommendations) =>
    (recommendations || []).length !== 0 && userData?.recommendationSetup?.show,
);

// in application radar view is rendered once
// fetchTrigger flag means if radar is current route or not
// skip this flag if not necessary

// TODO: is fetchTrigger really needed?
// As recommendations are only consumed in radar view useEffect whould be only triggred in said view?

export const useCardDeckData = (fetchTrigger = true) => {
  const dispatch = useDispatch();
  const recommendations = useSelector(recommendationsSelector);
  const currentCardIdRef = useRef(0);

  currentCardIdRef.current = recommendations[0]?.id;

  useEffect(() => {
    if (recommendations.length <= 5 && fetchTrigger) {
      dispatch(fetchRecommendationsIfNeeded(true, currentCardIdRef)).then();
    }
  }, [recommendations.length, currentCardIdRef, fetchTrigger, dispatch]);

  return recommendations;
};

export default { [sliceName]: RecommendationsReducer };
